import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { hubBrowse, hubGet, hubCreate, hubUpdate, hubDelete, hubMyPublications, hubMyPurchases, hubAddEpisode, hubDeleteEpisode, hubPurchase, hubPodcastRssUrl, cleanAiText } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BookOpen, Headphones, Mic, PenSquare, Search, Plus, Lock, Loader2, Play, Pause, ArrowLeft, Rss, ShoppingCart, Check, Trash2, Pencil, ExternalLink, Copy, Tag, Sparkles, Library } from 'lucide-react';

const KIND_META = {
  all:       { label: 'All',        icon: Library,    color: '#fff'    },
  ebook:     { label: 'Ebooks',     icon: BookOpen,   color: '#22d3ee' },
  audiobook: { label: 'Audiobooks', icon: Headphones, color: '#a78bfa' },
  workbook:  { label: 'Workbooks',  icon: PenSquare,  color: '#ffcf52' },
  podcast:   { label: 'Podcasts',   icon: Mic,        color: '#ff6b8a' },
};

function priceLabel(cents) {
  if (!cents) return 'Free';
  return '$' + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
}

// ===== Browse Page =====
export function HubBrowse() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (q.trim()) params.q = q.trim();
      setItems(await hubBrowse(params));
    } catch { toast.error('Failed to load hub'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  return (
    <div data-testid="hub-browse" className="max-w-[1280px] mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[#22d3ee]"><Library className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Knowledge Hub</span></div>
        <h1 className="font-display text-[36px] md:text-[54px] mt-2 leading-[1.02]">Ebooks, audiobooks, workbooks &amp; podcasts <br className="hidden md:block" /> from <span className="brand-gradient">working musicians.</span></h1>
        <p className="text-white/65 text-[15px] mt-3 max-w-[760px]">Every Academy track, every specialist session, every working artist's playbook — published here. Read, listen, learn, or publish your own.</p>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {Object.entries(KIND_META).map(([k, m]) => (
          <button key={k} onClick={() => setKind(k)} className={`chip ${kind === k ? 'active' : ''}`} data-testid={`hub-filter-${k}`}>
            <m.icon className="w-3.5 h-3.5 mr-1 inline" /> {m.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search…" className="input-field pl-9 w-[240px]" data-testid="hub-search" />
        </div>
        <button onClick={() => nav('/hub/publish')} className="btn-primary" data-testid="hub-publish-btn"><Plus className="w-4 h-4" /> Publish</button>
        <button onClick={() => nav('/hub/my')} className="btn-ghost" data-testid="hub-my-btn"><Sparkles className="w-4 h-4" /> My library</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-shell p-10 text-center">
          <Library className="w-10 h-10 mx-auto text-white/25" />
          <h3 className="font-display text-[22px] mt-3">No {kind !== 'all' ? KIND_META[kind].label.toLowerCase() : 'titles'} yet</h3>
          <p className="text-white/55 text-[13.5px] mt-1">Be the first to publish — or switch kind to explore.</p>
          <button onClick={() => nav('/hub/publish')} className="btn-primary mt-4"><Plus className="w-4 h-4" /> Publish one</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(a => <HubCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function HubCard({ a }) {
  const nav = useNavigate();
  const meta = KIND_META[a.kind] || KIND_META.ebook;
  const cover = a.cover_url || `https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=500&auto=format`;
  return (
    <button onClick={() => nav(`/hub/${a.id}`)} className="card-shell text-left hover-lift overflow-hidden flex flex-col" data-testid={`hub-card-${a.id}`}>
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={cover} alt={a.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(10,10,12,0.9))' }} />
        <div className="absolute top-3 left-3 chip text-[10px] uppercase" style={{ color: meta.color, borderColor: meta.color + '55' }}><meta.icon className="w-3 h-3 mr-1 inline" />{meta.label.replace(/s$/, '')}</div>
        {a.locked && <div className="absolute top-3 right-3 chip text-[10px] uppercase"><Lock className="w-3 h-3 mr-1 inline" /> paid</div>}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="font-display text-[18px] leading-tight line-clamp-2">{a.title}</div>
          {a.subtitle && <div className="text-[12px] text-white/70 mt-0.5 line-clamp-1">{a.subtitle}</div>}
        </div>
      </div>
      <div className="p-3 flex items-center justify-between">
        <span className="text-[13px] font-semibold">{priceLabel(a.price_cents)}</span>
        {a.tags && a.tags.slice(0, 2).map(t => <span key={t} className="text-[10px] text-white/50 ml-auto bg-white/5 px-1.5 py-0.5 rounded">{t}</span>)}
      </div>
    </button>
  );
}

// ===== Detail Page =====
export function HubDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useApp();
  const [sp] = useSearchParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  const load = async () => {
    try { setA(await hubGet(id)); } catch { toast.error('Not found'); nav('/hub'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (sp.get('purchased') === '1') {
      toast.success('Purchase complete — enjoy your content!');
      load();
    }
    // eslint-disable-next-line
  }, [sp]);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!a) return null;

  const meta = KIND_META[a.kind] || KIND_META.ebook;
  const owned = !a.locked;

  const purchase = async () => {
    if (!user) { toast.error('Sign in to purchase'); nav('/login'); return; }
    setBuying(true);
    try {
      const origin = window.location.origin;
      const r = await hubPurchase(a.id, { success_url: `${origin}/hub/${a.id}?purchased=1`, cancel_url: `${origin}/hub/${a.id}?cancelled=1` });
      if (r.free) { toast.success('Unlocked'); load(); }
      else if (r.checkout_url) window.location.href = r.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Checkout failed'); }
    finally { setBuying(false); }
  };

  return (
    <div data-testid="hub-detail" className="max-w-[1180px] mx-auto">
      <button onClick={() => nav('/hub')} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back to hub</button>
      <div className="grid lg:grid-cols-[340px_1fr] gap-8">
        <div>
          <div className="rounded-2xl overflow-hidden aspect-[4/5] bg-white/5 relative">
            <img src={a.cover_url || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800'} alt={a.title} className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3 chip text-[10px] uppercase" style={{ color: meta.color, borderColor: meta.color + '55' }}><meta.icon className="w-3 h-3 mr-1 inline" />{meta.label.replace(/s$/, '')}</div>
          </div>
          <div className="mt-4 card-shell p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/45">Price</div>
            <div className="font-display text-[28px]">{priceLabel(a.price_cents)}</div>
            {!owned && (
              <button onClick={purchase} disabled={buying} className="btn-primary w-full justify-center mt-3" data-testid="hub-purchase-btn">
                {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} {a.price_cents ? 'Buy &amp; unlock' : 'Unlock'}
              </button>
            )}
            {owned && <div className="chip active w-full justify-center mt-3"><Check className="w-3.5 h-3.5" /> You own this</div>}
            {a.kind === 'podcast' && (
              <button onClick={() => navigator.clipboard.writeText(hubPodcastRssUrl(a.id)).then(() => toast.success('RSS URL copied'))} className="btn-ghost w-full justify-center mt-2 text-[12.5px]" data-testid="hub-rss-copy">
                <Rss className="w-3.5 h-3.5" /> Copy RSS feed
              </button>
            )}
          </div>
          {a.publisher && (
            <div className="mt-4 card-shell p-4 flex items-center gap-3">
              <img src={a.publisher.avatar || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=100'} className="w-10 h-10 rounded-full object-cover" alt="" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/45">Publisher</div>
                <div className="font-semibold text-[14px]">{a.publisher.name}</div>
              </div>
            </div>
          )}
        </div>

        <div>
          <h1 className="font-display text-[40px] md:text-[54px] leading-[1.02]">{a.title}</h1>
          {a.subtitle && <p className="text-white/70 text-[16px] mt-2">{a.subtitle}</p>}
          <p className="text-white/75 text-[14.5px] mt-4 leading-relaxed whitespace-pre-wrap">{a.description}</p>

          {!owned && (
            <div className="card-shell p-5 mt-6 border-[#ffcf52]/40">
              <div className="flex items-center gap-2 text-[#ffcf52]"><Lock className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Locked preview</span></div>
              <p className="text-white/75 text-[13.5px] mt-1">Unlock to read / listen to the full content. {a.kind === 'podcast' && 'Episode list is visible; episode audio is gated.'}</p>
            </div>
          )}

          {/* Reader / Player */}
          {a.kind === 'ebook' && owned && a.body && <EbookReader body={a.body} />}
          {a.kind === 'workbook' && owned && (a.body || a.workbook_pdf_url) && <WorkbookView body={a.body} pdf={a.workbook_pdf_url} />}
          {a.kind === 'audiobook' && owned && a.audio_url && <AudioPlayer url={a.audio_url} title={a.title} />}
          {a.kind === 'podcast' && <PodcastEpisodes episodes={a.episodes || []} ownedPodcast={owned} />}
        </div>
      </div>
    </div>
  );
}

function EbookReader({ body }) {
  return (
    <article className="card-shell p-6 md:p-8 mt-6 text-white/90 text-[15px] leading-[1.8] whitespace-pre-wrap" data-testid="hub-ebook-reader">{cleanAiText(body)}</article>
  );
}

function WorkbookView({ body, pdf }) {
  return (
    <div className="mt-6 space-y-4" data-testid="hub-workbook-view">
      {pdf && <a href={pdf} target="_blank" rel="noreferrer" className="btn-primary w-fit"><ExternalLink className="w-4 h-4" /> Open PDF</a>}
      {body && <article className="card-shell p-6 md:p-8 text-white/90 text-[14.5px] leading-[1.75] whitespace-pre-wrap">{cleanAiText(body)}</article>}
    </div>
  );
}

function AudioPlayer({ url, title }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch { toast.error('Unable to play'); } }
  };
  return (
    <div className="card-shell p-6 mt-6" data-testid="hub-audio-player">
      <div className="flex items-center gap-4">
        <button onClick={toggle} className="w-16 h-16 rounded-full grid place-items-center bg-gradient-to-br from-[#a78bfa] to-[#ff2e63]">{playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}</button>
        <div className="flex-1">
          <div className="font-display text-[18px]">{title}</div>
          <audio ref={audioRef} src={url} controls className="w-full mt-2" onEnded={() => setPlaying(false)} />
        </div>
      </div>
    </div>
  );
}

function PodcastEpisodes({ episodes, ownedPodcast }) {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const toggle = async (ep) => {
    if (!audioRef.current) return;
    if (playingId === ep.id) { audioRef.current.pause(); setPlayingId(null); return; }
    audioRef.current.src = ep.audio_url;
    try { await audioRef.current.play(); setPlayingId(ep.id); } catch {}
  };
  return (
    <div className="mt-6" data-testid="hub-podcast-episodes">
      <h3 className="font-display text-[22px] mb-3">Episodes ({episodes.length})</h3>
      {episodes.length === 0 && <div className="text-white/55 text-[13.5px]">No episodes published yet.</div>}
      <div className="space-y-2">
        {episodes.map(ep => (
          <div key={ep.id} className="card-shell p-3 flex items-center gap-3" data-testid={`hub-episode-${ep.id}`}>
            <button onClick={() => ownedPodcast ? toggle(ep) : toast.error('Unlock to play')} className="w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-[#a78bfa]/30 disabled:opacity-40" disabled={!ownedPodcast}>
              {ownedPodcast ? (playingId === ep.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />) : <Lock className="w-3.5 h-3.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/50">Ep {ep.episode_number}</div>
              <div className="font-semibold text-[14px] truncate">{ep.title}</div>
              {ep.description && <div className="text-[12.5px] text-white/60 line-clamp-1">{ep.description}</div>}
            </div>
            <div className="text-[11.5px] text-white/50 tabular-nums">{Math.floor((ep.duration_s || 0) / 60)}m</div>
          </div>
        ))}
      </div>
      <audio ref={audioRef} preload="none" onEnded={() => setPlayingId(null)} />
    </div>
  );
}

// ===== Publish Page =====
export function HubPublish() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);
  const [form, setForm] = useState({ kind: 'ebook', title: '', subtitle: '', description: '', cover_url: '', price_cents: 0, tier_required: 'free', tags: '', body: '', audio_url: '', workbook_pdf_url: '', status: 'draft' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    hubGet(id).then(a => setForm({
      kind: a.kind, title: a.title, subtitle: a.subtitle || '', description: a.description, cover_url: a.cover_url || '',
      price_cents: a.price_cents || 0, tier_required: a.tier_required || 'free',
      tags: (a.tags || []).join(', '), body: a.body || '', audio_url: a.audio_url || '',
      workbook_pdf_url: a.workbook_pdf_url || '', status: a.status,
    })).catch(() => toast.error('Failed to load'));
  }, [id, editing]);

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error('Title and description required');
    setBusy(true);
    try {
      const payload = {
        ...form,
        price_cents: Math.max(0, parseInt(form.price_cents) || 0),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      const r = editing ? await hubUpdate(id, payload) : await hubCreate(payload);
      toast.success(editing ? 'Saved' : (form.status === 'published' ? 'Published!' : 'Draft saved'));
      nav(`/hub/${r.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="hub-publish" className="max-w-[900px] mx-auto">
      <button onClick={() => nav(editing ? `/hub/${id}` : '/hub')} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <h1 className="font-display text-[34px] mb-4">{editing ? 'Edit publication' : 'Publish to Knowledge Hub'}</h1>

      <div className="card-shell p-6 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Kind</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(KIND_META).filter(([k]) => k !== 'all').map(([k, m]) => (
              <button key={k} onClick={() => upd('kind', k)} className={`chip ${form.kind === k ? 'active' : ''}`} data-testid={`hub-pub-kind-${k}`}>
                <m.icon className="w-3.5 h-3.5 mr-1 inline" /> {m.label.replace(/s$/, '')}
              </button>
            ))}
          </div>
        </div>

        <input value={form.title} onChange={e => upd('title', e.target.value)} placeholder="Title" className="input-field" data-testid="hub-pub-title" />
        <input value={form.subtitle} onChange={e => upd('subtitle', e.target.value)} placeholder="Subtitle (optional)" className="input-field" />
        <textarea value={form.description} onChange={e => upd('description', e.target.value)} placeholder="Description / summary" rows={4} className="input-field resize-none" data-testid="hub-pub-desc" />
        <input value={form.cover_url} onChange={e => upd('cover_url', e.target.value)} placeholder="Cover image URL" className="input-field" />

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Price (cents, 0 = free)</div>
            <input type="number" min={0} value={form.price_cents} onChange={e => upd('price_cents', e.target.value)} className="input-field" data-testid="hub-pub-price" />
          </div>
          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Status</div>
            <select value={form.status} onChange={e => upd('status', e.target.value)} className="input-field" data-testid="hub-pub-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <input value={form.tags} onChange={e => upd('tags', e.target.value)} placeholder="tags, comma separated" className="input-field" />

        {(form.kind === 'ebook' || form.kind === 'workbook') && (
          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Body (markdown-ish, displayed as plain rich text)</div>
            <textarea value={form.body} onChange={e => upd('body', e.target.value)} rows={14} className="input-field resize-none font-mono text-[13px]" placeholder="# Chapter 1..." data-testid="hub-pub-body" />
          </div>
        )}
        {form.kind === 'audiobook' && (
          <input value={form.audio_url} onChange={e => upd('audio_url', e.target.value)} placeholder="Audio file URL (MP3)" className="input-field" data-testid="hub-pub-audio" />
        )}
        {form.kind === 'workbook' && (
          <input value={form.workbook_pdf_url} onChange={e => upd('workbook_pdf_url', e.target.value)} placeholder="Workbook PDF URL (optional — defaults to body text)" className="input-field" />
        )}

        <div className="flex justify-end gap-2">
          {editing && <DeleteBtn id={id} nav={nav} />}
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="hub-pub-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {editing ? 'Save' : (form.status === 'published' ? 'Publish' : 'Save draft')}
          </button>
        </div>
      </div>

      {editing && form.kind === 'podcast' && <EpisodeManager assetId={id} />}
    </div>
  );
}

function DeleteBtn({ id, nav }) {
  const [busy, setBusy] = useState(false);
  const rm = async () => {
    if (!window.confirm('Delete this publication and all its episodes?')) return;
    setBusy(true);
    try { await hubDelete(id); toast.success('Deleted'); nav('/hub/my'); }
    catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };
  return <button onClick={rm} disabled={busy} className="btn-ghost text-[#ff6b8a] disabled:opacity-50"><Trash2 className="w-4 h-4" /> Delete</button>;
}

function EpisodeManager({ assetId }) {
  const [episodes, setEpisodes] = useState([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [audio, setAudio] = useState('');
  const [dur, setDur] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const a = await hubGet(assetId); setEpisodes(a.episodes || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetId]);

  const add = async () => {
    if (!title.trim() || !audio.trim()) return toast.error('Title and audio URL required');
    setBusy(true);
    try {
      await hubAddEpisode(assetId, { title, description: desc, audio_url: audio, duration_s: parseInt(dur) || 0 });
      setTitle(''); setDesc(''); setAudio(''); setDur(0);
      toast.success('Episode added');
      load();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const rm = async (epId) => {
    if (!window.confirm('Delete episode?')) return;
    try { await hubDeleteEpisode(epId); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="card-shell p-6 mt-6" data-testid="hub-episodes-manager">
      <div className="flex items-center gap-2 text-[#ff6b8a]"><Mic className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Episodes</span></div>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Episode title" className="input-field" data-testid="episode-title" />
        <input value={audio} onChange={e => setAudio(e.target.value)} placeholder="Audio URL (MP3)" className="input-field" data-testid="episode-audio" />
        <input type="number" value={dur} onChange={e => setDur(e.target.value)} placeholder="Duration (seconds)" className="input-field" />
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Show notes (optional)" rows={1} className="input-field" />
      </div>
      <button onClick={add} disabled={busy} className="btn-primary mt-3 disabled:opacity-50" data-testid="episode-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add episode</button>
      <div className="mt-4 space-y-2">
        {episodes.map(ep => (
          <div key={ep.id} className="flex items-center gap-3 border border-white/10 rounded-lg p-2">
            <div className="flex-1"><div className="text-[13px] font-semibold">#{ep.episode_number} — {ep.title}</div></div>
            <button onClick={() => rm(ep.id)} className="text-white/50 hover:text-[#ff6b8a]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== My Library =====
export function HubMy() {
  const nav = useNavigate();
  const [pubs, setPubs] = useState([]);
  const [purs, setPurs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([hubMyPublications().catch(() => []), hubMyPurchases().catch(() => [])])
      .then(([a, b]) => { setPubs(a); setPurs(b); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="hub-my" className="max-w-[1180px] mx-auto">
      <button onClick={() => nav('/hub')} className="btn-ghost text-[13px] mb-4"><ArrowLeft className="w-4 h-4" /> Back to hub</button>
      <h1 className="font-display text-[36px] mb-6">My library</h1>

      <Tabs defaultValue="pubs">
        <TabsList className="bg-white/[0.04] border border-white/10">
          <TabsTrigger value="pubs">My publications ({pubs.length})</TabsTrigger>
          <TabsTrigger value="purs">My purchases ({purs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pubs" className="mt-6">
          {pubs.length === 0 ? (
            <div className="card-shell p-8 text-center">
              <p className="text-white/65">Nothing published yet.</p>
              <button onClick={() => nav('/hub/publish')} className="btn-primary mt-3"><Plus className="w-4 h-4" /> Publish</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pubs.map(a => (
                <div key={a.id} className="card-shell p-4" data-testid={`hub-mypub-${a.id}`}>
                  <div className="flex items-center gap-2"><span className="chip text-[10px] uppercase">{a.kind}</span><span className={`chip text-[10px] ${a.status === 'published' ? 'active' : ''}`}>{a.status}</span></div>
                  <div className="font-semibold mt-2 line-clamp-2">{a.title}</div>
                  <div className="text-[12.5px] text-white/55 mt-1">{priceLabel(a.price_cents)}</div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => nav(`/hub/${a.id}`)} className="btn-ghost text-[12px]">View</button>
                    <button onClick={() => nav(`/hub/publish/${a.id}`)} className="btn-ghost text-[12px]"><Pencil className="w-3 h-3" /> Edit</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="purs" className="mt-6">
          {purs.length === 0 ? <div className="text-white/55">No purchases yet.</div> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {purs.map(p => <HubCard key={p.asset.id} a={p.asset} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
